import { useMemo, useState } from 'react';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Callout } from '@/components/ui/callout';
import { SectionLabel } from '@/components/ui/section-label';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';

const CATEGORIES = [
  { value: 'bug', labelKey: 'contact.form.categories.bug' },
  { value: 'question', labelKey: 'contact.form.categories.question' },
  { value: 'improvement', labelKey: 'contact.form.categories.improvement' },
  { value: 'other', labelKey: 'contact.form.categories.other' },
] as const;

export type ContactCategory = (typeof CATEGORIES)[number]['value'];

const encodeForMailto = (value: string) => encodeURIComponent(value).replace(/%20/g, '%20');

type ContactFormCardProps = {
  contactEmail: string;
  userEmail?: string | null;
  onBack: () => void;
};

export function ContactFormCard({ contactEmail, userEmail, onBack }: ContactFormCardProps) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<ContactCategory>('bug');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [error, setError] = useState('');

  const isDisabled = useMemo(
    () => isSubmitting || !subject.trim() || !message.trim(),
    [isSubmitting, message, subject]
  );

  const categoryLabel = useMemo(() => {
    const option = CATEGORIES.find((item) => item.value === category);
    if (!option) {
      return t('contact.form.categories.fallback');
    }
    return t(option.labelKey);
  }, [category, t]);

  const categoryOptions = useMemo(
    () => CATEGORIES.map((option) => ({ value: option.value, label: t(option.labelKey) })),
    [t]
  );

  const authorEmail =
    userEmail && userEmail.length > 0 ? userEmail : t('contact.form.emailFallback');

  const mailtoHref = useMemo(() => {
    const mailSubject = t('contact.form.mail.subject', { category: categoryLabel, subject });
    const mailBody = t('contact.form.mail.body', {
      category: categoryLabel,
      author: authorEmail,
      message,
    });

    return `mailto:${contactEmail}?subject=${encodeForMailto(mailSubject)}&body=${encodeForMailto(mailBody)}`;
  }, [authorEmail, categoryLabel, contactEmail, message, subject, t]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (isDisabled) return;

    setIsSubmitting(true);
    setStatus('idle');
    setError('');

    try {
      const newWindow = window.open(mailtoHref, '_blank');

      if (!newWindow) {
        window.location.href = mailtoHref;
      }

      setStatus('success');
    } catch {
      setError(t('contact.form.status.errorDefault'));
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      if (typeof window === 'undefined' || !window.navigator?.clipboard) {
        throw new Error(t('contact.form.copy.notAvailable'));
      }

      await window.navigator.clipboard.writeText(contactEmail);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    } finally {
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setCategory('bug');
    setStatus('idle');
    setError('');
  };

  return (
    <Card as="aside" className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <SectionLabel>{t('contact.form.badge')}</SectionLabel>
        <h2 className="section-title">{t('contact.form.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('contact.form.description')}</p>
      </header>

      <Card
        tier="muted"
        padding="none"
        className="flex flex-col gap-2 p-3 text-sm text-muted-foreground"
      >
        <div className="flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-full bg-card shadow-sm">
            <Mail className="h-4 w-4 text-primary" aria-hidden />
          </span>
          <div>
            <p className="font-semibold text-foreground">{t('contact.form.contactInfo.title')}</p>
            <a
              href={`mailto:${contactEmail}`}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              {contactEmail}
            </a>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCopyEmail}
          className="self-start"
        >
          {copyStatus === 'copied'
            ? t('contact.form.contactInfo.copySuccess')
            : t('contact.form.contactInfo.copyCta')}
        </Button>
        {copyStatus === 'error' && (
          <Callout tone="error" size="inline">
            {t('contact.form.contactInfo.copyError')}
          </Callout>
        )}
      </Card>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="contact-subject" className="text-sm text-foreground">
            {t('contact.form.fields.subjectLabel')}
          </Label>
          <Input
            id="contact-subject"
            placeholder={t('contact.form.fields.subjectPlaceholder')}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contact-category" className="text-sm text-foreground">
            {t('contact.form.fields.categoryLabel')}
          </Label>
          <SimpleSelect
            id="contact-category"
            value={category}
            onChange={(value) => value && setCategory(value as ContactCategory)}
            options={categoryOptions}
            placeholder={t('contact.form.fields.categoryPlaceholder')}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contact-message" className="text-sm text-foreground">
            {t('contact.form.fields.messageLabel')}
          </Label>
          <Textarea
            id="contact-message"
            placeholder={t('contact.form.fields.messagePlaceholder')}
            rows={4}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            required
          />
        </div>

        {status === 'success' && (
          <Callout tone="success" title={t('contact.form.status.successTitle')}>
            {t('contact.form.status.successDescription')}
          </Callout>
        )}

        {status === 'error' && error && <Callout tone="error">{error}</Callout>}

        <div className="mt-auto flex flex-col gap-2">
          <Button type="submit" className="w-full" disabled={isDisabled} loading={isSubmitting}>
            {isSubmitting ? (
              t('contact.form.status.opening')
            ) : (
              <>
                <Send aria-hidden /> {t('contact.form.actions.openClient')}
              </>
            )}
          </Button>

          <Button type="button" variant="ghost" onClick={resetForm}>
            {t('contact.form.actions.reset')}
          </Button>
          <Button type="button" variant="outline" onClick={onBack}>
            <ArrowLeft aria-hidden /> {t('contact.form.actions.back')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
